import { supabase } from '../lib/supabase';

const BUCKET_NAME = 'project-images';

class ProjectService {
  // Get all projects
  async getAllProjects() {
    try {
      const { data, error } = await supabase?.from('projects')?.select(`
        *,
        clients (company_name)
      `)?.order('created_at', { ascending: false });

      if (error) throw error;
      return { data, error: null };
    } catch (error) {
      console.error('Error fetching projects:', error);
      return { data: null, error: error?.message };
    }
  }

  // Get project by ID
  async getProjectById(id) {
    try {
      const { data, error } = await supabase?.from('projects')?.select('*')?.eq('id', id)?.single();

      if (error) throw error;
      return { data, error: null };
    } catch (error) {
      console.error('Error fetching project:', error);
      return { data: null, error: error?.message };
    }
  }

  // Create new project
  async createProject(projectData) {
    try {
      const { data, error } = await supabase?.from('projects')?.insert([{
          name: projectData?.name,
          project_no: projectData?.projectNo || null,
          description: projectData?.description,
          owner: projectData?.owner,
          team_members: projectData?.teamMembers || [],
          status: 'planning',
          progress: 0,
          workflow_count: 0,
          image_url: projectData?.imageUrl || null,
          client_id: projectData?.clientId || null,
          project_type: projectData?.projectType || null,
          project_country: projectData?.projectCountry || null
        }])?.select()?.single();

      if (error) throw error;
      return { data, error: null };
    } catch (error) {
      console.error('Error creating project:', error);
      return { data: null, error: error?.message };
    }
  }

  // Update project
  async updateProject(id, projectData) {
    try {
      const updateData = {
        name: projectData?.name,
        project_no: projectData?.projectNo || null,
        description: projectData?.description,
        owner: projectData?.owner,
        team_members: projectData?.teamMembers || []
      };

      if (projectData?.imageUrl !== undefined) {
        updateData.image_url = projectData?.imageUrl;
      }

      if (projectData?.clientId !== undefined) {
        updateData.client_id = projectData?.clientId || null;
      }

      if (projectData?.projectType !== undefined) {
        updateData.project_type = projectData?.projectType || null;
      }

      if (projectData?.projectCountry !== undefined) {
        updateData.project_country = projectData?.projectCountry || null;
      }

      const { data, error } = await supabase?.from('projects')?.update(updateData)?.eq('id', id)?.select()?.single();

      if (error) throw error;
      return { data, error: null };
    } catch (error) {
      console.error('Error updating project:', error);
      return { data: null, error: error?.message };
    }
  }

  // Delete project
  async deleteProject(id) {
    try {
      const { error } = await supabase?.from('projects')?.delete()?.eq('id', id);

      if (error) throw error;
      return { error: null };
    } catch (error) {
      console.error('Error deleting project:', error);
      return { error: error?.message };
    }
  }

  // Upload project image
  async uploadProjectImage(file, projectId) {
    try {
      const fileExt = file?.name?.split('.')?.pop();
      const fileName = `${projectId}-${Date.now()}.${fileExt}`;
      const filePath = `${fileName}`;

      const { data, error } = await supabase?.storage?.from(BUCKET_NAME)?.upload(filePath, file, {
          cacheControl: '3600',
          upsert: true
        });

      if (error) throw error;

      // Get public URL
      const { data: urlData } = supabase?.storage?.from(BUCKET_NAME)?.getPublicUrl(filePath);

      return { data: { path: filePath, url: urlData?.publicUrl }, error: null };
    } catch (error) {
      console.error('Error uploading image:', error);
      return { data: null, error: error?.message };
    }
  }

  // Delete project image
  async deleteProjectImage(filePath) {
    try {
      const { error } = await supabase?.storage?.from(BUCKET_NAME)?.remove([filePath]);

      if (error) throw error;
      return { error: null };
    } catch (error) {
      console.error('Error deleting image:', error);
      return { error: error?.message };
    }
  }

  // Get public URL for image
  getPublicImageUrl(filePath) {
    if (!filePath) return null;
    const { data } = supabase?.storage?.from(BUCKET_NAME)?.getPublicUrl(filePath);
    return data?.publicUrl || null;
  }
}

export default new ProjectService();